import {
  Box,
  FormControl,
  FormErrorMessage,
  Flex,
  Icon,
  Input,
  Checkbox,
} from '@chakra-ui/react';
import { Field, FieldArray } from 'formik';
import { isInvalidField } from '@/lib/util';
import { renderErrors } from '@/ui/util';
import { Stars, Range, YesNo } from './';
import { IconButton } from '@/components/ats';
import { FaPlus } from 'react-icons/fa';
import SvgDragAndDrop from '@/assets/icons/table/DragAndDrop';
import { Draggable } from 'react-beautiful-dnd';
import { OpenQuestion } from './OpenQuestion';
import { Trash01 } from '@kula-ai/icons/outline/Trash01';
import { KText } from '@/ui-library/components/text';
import DescriptionField from '@/components/score-card-builder/Description';

const scoreCards = [
  {
    id: 'star',
    label: 'Score (stars)',
    showAfter: true,
    placeholder: 'What are you measuring?',
    AfterComp: Stars,
  },
  {
    id: 'one_to_ten',
    label: 'Score (1 to 10)',
    showAfter: true,
    placeholder: 'What are you measuring?',
    AfterComp: Range,
  },
  {
    id: 'yes_no',
    label: 'Yes/No',
    showAfter: true,
    placeholder: 'Write a question that can be answered with a Yes or a No',
    AfterComp: YesNo,
  },
  {
    id: 'open_question',
    label: 'Open question',
    showAfter: true,
    placeholder: 'Write an open question',
    AfterComp: OpenQuestion,
  },
  {
    id: 'multiple_choice',
    label: 'Single select',
    showAfter: false,
    placeholder: 'Write a single select question',
  },
  {
    id: 'checkbox',
    label: 'Multiple select',
    showAfter: false,
    placeholder: 'Write a multiple select question',
  },
];

export const ScoreCardContainer = ({
  item = {},
  formik = {},
  index,
  arrayHelpers,
}) => {
  const { type } = item;
  const currentScoreCard = scoreCards.find((item) => item?.id === type);
  const CurrScoreCardAfterComp = currentScoreCard?.AfterComp;

  const inputStyles = (meta) => {
    return {
      fontSize: 'sm',
      paddingInlineStart: '.75em',
      pt: '1.125em',
      pb: '1.125em',
      height: '3.5em',
      border: meta?.error ? '2px solid !important' : '2px solid !important',
      borderColor: meta?.error
        ? '#A32020 !important'
        : `var(--kula-colors-border-color) !important`,
      borderRadius: '8px',
      _placeholder: {
        opacity: 1,
        fontSize: 'sm',
        fontWeight: 'medium',
        color: 'secondary-500',
      },
    };
  };

  return (
    <Draggable
      key={item?.id}
      draggableId={`${item?.id}`}
      index={index}
      disableInteractiveElementBlocking
    >
      {(provided, snapshot) => (
        <Box
          ref={provided.innerRef}
          {...provided.draggableProps}
          style={{
            ...provided.draggableProps.style,
            background: snapshot.isDragging && '#fff',
            left: snapshot.isDragging && '0px',
            boxShadow:
              snapshot.isDragging &&
              '0px 3px 8px 0px #ABABAB1A, 0px 14px 14px 0px #ABABAB17, 0px 31px 19px 0px #ABABAB0D, 0px 55px 22px 0px #ABABAB03, 0px 86px 24px 0px #ABABAB00',
          }}
          border="1px solid"
          borderColor="border-color"
          p="12px"
          borderRadius="sm"
          pb="20px"
          mb={'10px'}
          data-test-id="score-card-question-container"
        >
          <Flex alignItems="center" justifyContent="space-between" mb="12px">
            <Flex>
              <Box
                {...provided.dragHandleProps}
                cursor={snapshot.isDragging ? 'grabbing' : 'grab'}
              >
                <Icon
                  as={SvgDragAndDrop}
                  width="14px"
                  height="14px"
                  mr="md"
                  verticalAlign={'baseline'}
                />
              </Box>
              <KText size="sm-medium">{currentScoreCard?.label}</KText>
            </Flex>
            <Flex alignItems="center" gap="12px">
              <Flex as="label" gap="12px" alignItems="center" cursor="pointer">
                <Field name={`scoreCards.${index}.mandatory`}>
                  {({ field }) => {
                    return (
                      <FormControl
                        gap="6px"
                        display={'flex'}
                        alignItems={'center'}
                      >
                        <Checkbox
                          {...field}
                          defaultChecked={
                            field?.value === 'true' || field?.value === true
                              ? true
                              : false
                          }
                        />
                        <KText
                          size="sm-medium"
                          data-test-id="mark-question-as-mandatory"
                        >
                          Mandatory
                        </KText>
                      </FormControl>
                    );
                  }}
                </Field>
              </Flex>
              <Icon
                as={Trash01}
                onClick={() => {
                  arrayHelpers?.remove(index);
                }}
                cursor="pointer"
                data-test-id="delete-question"
              />
            </Flex>
          </Flex>
          <Flex flexDir="column" mx="14px">
            <Field name={`scoreCards.${index}.question`}>
              {({ field, form, meta }) => {
                return (
                  <FormControl
                    isInvalid={isInvalidField(form, meta)}
                    isRequired={true}
                  >
                    <Input
                      {...field}
                      p="14px"
                      placeholder={currentScoreCard?.placeholder || ''}
                      data-test-id="scorecard-question-name"
                      value={field?.value || ''}
                      variant="outline"
                      {...inputStyles(meta)}
                      maxLength={255}
                    />
                    <FormErrorMessage flexDir="column" alignItems="flex-start">
                      {renderErrors(field?.label, meta?.error)}
                    </FormErrorMessage>
                  </FormControl>
                );
              }}
            </Field>
            <Box mt="12px">
              <DescriptionField name={`scoreCards.${index}.description`} />
            </Box>
          </Flex>
          {currentScoreCard?.showAfter && CurrScoreCardAfterComp && (
            <Flex mx="14px" mt="12px">
              <CurrScoreCardAfterComp />
            </Flex>
          )}
          {(item?.type === 'multiple_choice' || item?.type === 'checkbox') && (
            <Flex flexDir="column" ml="14px" mt="12px" gap="12px">
              <FieldArray
                name={`scoreCards.${index}.options`}
                render={(optArrayHelpers) => (
                  <>
                    {item?.options?.map((opt, optionIndex) => (
                      <Field
                        name={`scoreCards.${index}.options.${optionIndex}.optionText`}
                      >
                        {({ field, form, meta }) => (
                          <Flex alignItems="center" gap="12px">
                            <Box
                              minW="24px"
                              height="24px"
                              borderRadius={
                                item?.type === 'multiple_choice' ? 'full' : 'xs'
                              }
                              border="2px solid"
                              borderColor={
                                meta?.error ? '#A32020' : 'border-color'
                              }
                              marginBottom={meta?.error ? 'lg' : '0px'}
                            />
                            <FormControl
                              isInvalid={isInvalidField(form, meta)}
                              isRequired={true}
                            >
                              <Input
                                {...field}
                                p="14px"
                                placeholder={'Write a choice'}
                                value={field?.value || ''}
                                variant="outline"
                                data-test-id="scorecard-question-option"
                                {...inputStyles(meta)}
                                maxLength={255}
                              />
                              <FormErrorMessage
                                flexDir="column"
                                alignItems="flex-start"
                              >
                                {renderErrors(field?.label, meta?.error)}
                              </FormErrorMessage>
                            </FormControl>
                            <Icon
                              as={Trash01}
                              onClick={() => {
                                optArrayHelpers?.remove(optionIndex);
                              }}
                              cursor="pointer"
                            />
                          </Flex>
                        )}
                      </Field>
                    ))}
                    {typeof formik?.errors?.scoreCards?.[index]?.options ===
                      'string' && (
                      <Flex flexDir="column" alignItems="flex-start">
                        <KText color="error-200">
                          {formik?.errors?.scoreCards?.[index]?.options}
                        </KText>
                      </Flex>
                    )}

                    <IconButton
                      icon={FaPlus}
                      text="Add option"
                      type="button"
                      onClick={() => {
                        optArrayHelpers.push({ optionText: '' });
                      }}
                      width="fit-content"
                    />
                  </>
                )}
              />
            </Flex>
          )}
        </Box>
      )}
    </Draggable>
  );
};
